/**
 * [STEP 2-B] Asset Resolver — 3D 정의의 자산 칸 → 캐시 열쇠(identity) + 내려받기 주소.
 *
 *   Twin Definition(asset.url | asset.asset_id) → resolveAssetRef → ResolvedAsset{key, url} → Asset Cache(열쇠로) → GLTFLoader(주소로)
 *
 *   · 정적 개발용  url: "/twin-assets/x.glb"  → key "url:/twin-assets/x.glb" · 주소 = 그 경로
 *   · 운영용       asset_id: "<uuid>"         → key "asset:<uuid 소문자>" · 주소 = 서버가 준 임시 주소(서명 URL — STEP 2-C)
 *     주소는 서버 응답(twin-palette · 모델 상세의 asset_urls)이 registerAssetUrls 로 넣는다. DB · 정의에는 저장하지 않는다.
 *     주소가 바뀌어도 열쇠는 같다 → 이미 ready 면 다시 받지 않는다(캐시는 열쇠만 본다).
 *   · 주소가 아직 없는 asset_id = null(불러오는 중 상자) — 나중에 주소가 오면 nudgeAsset 으로 다시 그리게 한다.
 *     무한 「불러오는 중」 금지: 서버가 주소를 안 준다고 확정(url: null)하면 바로 error · 서버 응답에 아예 없으면
 *     UNRESOLVED_MS(짧은 대기) 뒤 error(이유 표시). 나중에 진짜 주소가 오면 error 를 지우고 다시 받는다.
 *   · 보조 값: 서버가 준 검사 결과(inspection — crm_catalog_model_assets 정본)를 함께 기억(치수 표시의 대체값).
 *   · [STEP 2-C] 서명 주소 만료: expires_at - 60초가 지났거나 불러오기가 400/401/403 으로 실패하면
 *     GET asset-urls 로 새 주소를 받아 → registerAssetUrls → 그 열쇠만 invalidate → 다시 받는다. 열쇠마다 **한 번만**
 *     (그래도 실패하면 error 그대로 — 무한 재시도 없음). 받기에 성공하면(ready) 그 열쇠의 1회 기회를 되살린다(다음 만료 대비).
 *   · 주소 출처: 서버가 응답에 실어 준 storage_origin(SUPABASE_URL) 만 허용(assetCache.allowAssetOrigin).
 */
import { allowAssetOrigin, failAsset, invalidateAsset as invalidateCached, nudgeAsset, onAssetSettled, peekAsset } from "./assetCache";
import { apiFetch } from "../../../../lib/apiFetch";
import type { AssetGeometry } from "./types";

export interface ResolvedAsset {
  key: string;
  url: string;
  assetId?: string;
  expiresAt?: string;
}

/** crm_catalog_model_assets.inspection (version 1) — 업로드 검사(STEP 2-C)가 채운다. 이번 단계는 읽기만 */
export interface AssetInspection {
  version: number;
  bounds?: { min: [number, number, number]; max: [number, number, number]; size?: { width_m: number; depth_m: number; height_m: number } };
  node_count?: number; mesh_count?: number; triangle_count?: number; texture_count?: number; animation_count?: number;
  has_skin?: boolean; extensions?: string[]; warnings?: string[];
  /** [STEP 2-C] 거부 이유(rejected) · POSITION min/max 가 모두 있어 bounds 가 완전한가 */
  errors?: string[]; bounds_complete?: boolean;
}

/** 서버 응답의 asset_urls 한 칸 */
export interface AssetUrlEntry { url: string | null; expires_at?: string | null; inspection?: AssetInspection | null }

interface Known { url: string | null; expiresAt?: string; inspection: AssetInspection | null }
const known = new Map<string, Known>();
/** 주소를 기다리는 최대 시간 — 지나면 「실패: 주소를 받지 못함」 */
let UNRESOLVED_MS = 8000;
const waiting = new Map<string, ReturnType<typeof setTimeout>>();
export const REASON_NO_URL = "내려받기 주소가 없습니다 — 서버가 이 자산의 주소를 주지 않았습니다(Storage 서명은 STEP 2-C)";
export const REASON_TIMEOUT = "내려받기 주소를 받지 못했습니다 — 화면의 자산 목록에 이 asset_id 가 없습니다(없거나 준비되지 않은 자산)";

/** 만료 몇 ms 전부터 새 주소를 받을지 */
const EXPIRY_MARGIN_MS = 60 * 1000;
/** 새 주소 받기 — 기본 = GET asset-urls(로그인 사용자). 테스트는 setAssetUrlRefresherForTest */
export type AssetUrlRefresher = (assetIds: string[]) => Promise<{ asset_urls?: Record<string, AssetUrlEntry | null>; storage_origin?: string | null } | null>;
const defaultRefresher: AssetUrlRefresher = (ids) => apiFetch(`/api/equipment-catalog-handler?resource=asset-urls&ids=${encodeURIComponent(ids.join(","))}`);
let refresher: AssetUrlRefresher = defaultRefresher;
/** 열쇠마다 새 주소 받기 1회 — 쓴 열쇠(ready 가 되면 지움) */
const refreshUsed = new Set<string>();
const refreshing = new Map<string, Promise<boolean>>();

export const assetKeyForId = (assetId: string) => `asset:${assetId.toLowerCase()}`;
export const assetKeyForUrl = (url: string) => `url:${url}`;

/** 정의의 자산 칸 → 열쇠(주소와 무관 — 같은 자산이면 늘 같다). 자산 칸이 아니면 null */
export function assetKeyOf(a: Pick<AssetGeometry, "url" | "asset_id"> | null | undefined): string | null {
  if (!a) return null;
  if (typeof a.asset_id === "string" && a.asset_id) return assetKeyForId(a.asset_id);
  if (typeof a.url === "string" && a.url) return assetKeyForUrl(a.url);
  return null;
}

/**
 * 서버 응답의 asset_urls 를 넣는다 — 새 주소가 생긴 열쇠는 다시 그리게 알린다(ready 인 자산은 주소가 바뀌어도 다시 받지 않음).
 *   storageOrigin = 응답의 storage_origin(이 출처의 Storage 주소만 허용)
 */
export function registerAssetUrls(map: Record<string, AssetUrlEntry | null | undefined> | null | undefined, storageOrigin?: string | null) {
  allowAssetOrigin(storageOrigin);
  if (!map || typeof map !== "object") return;
  for (const [id, v] of Object.entries(map)) {
    if (!id || !v || typeof v !== "object") continue;
    const key = assetKeyForId(id);
    const prev = known.get(key);
    const next: Known = { url: typeof v.url === "string" && v.url ? v.url : null, expiresAt: typeof v.expires_at === "string" ? v.expires_at : undefined, inspection: v.inspection && typeof v.inspection === "object" ? v.inspection : (prev ? prev.inspection : null) };
    known.set(key, next);
    const w = waiting.get(key); if (w) { clearTimeout(w); waiting.delete(key); }
    if (!next.url) { failAsset(key, REASON_NO_URL); continue; } // 서버가 주소 없음을 확정 — 무한 대기 대신 실패
    if (!prev || !prev.url || prev.url !== next.url) {
      // 주소가 처음 생겼거나 바뀜(새 서명) — 실패였으면 지우고 다시 받게 · 아직 안 받았으면 다시 그리게. ready 는 그대로
      const st = peekAsset(key).status;
      if (st === "error") invalidateCached(key); else if (st === "idle") nudgeAsset(key);
    }
  }
}

/**
 * build 가 주소 없는 asset_id 를 만났을 때 — 짧게 기다렸다가 그래도 없으면 error(무한 불러오는 중 금지). 열쇠마다 타이머 하나
 */
export function noteUnresolved(key: string) {
  if (waiting.has(key) || peekAsset(key).status !== "idle") return;
  const k = known.get(key);
  if (k && !k.url) { failAsset(key, REASON_NO_URL); return; }
  waiting.set(key, setTimeout(() => {
    waiting.delete(key);
    const now = known.get(key);
    if (!now || !now.url) failAsset(key, REASON_TIMEOUT);
  }, UNRESOLVED_MS));
}

/** 만료가 가까운가(expires_at - 60초 지남) */
function expiringSoon(k: Known, now = Date.now()) {
  if (!k.expiresAt) return false;
  const t = Date.parse(k.expiresAt);
  return Number.isFinite(t) && now >= t - EXPIRY_MARGIN_MS;
}

/**
 * [STEP 2-C] 새 서명 주소 받기 — 열쇠마다 한 번. true = 새 주소를 받아 넣었다(그 뒤 registerAssetUrls 가 다시 받게 함)
 *   이미 썼거나 받지 못하면 false — 부른 쪽은 지금 상태(error)를 그대로 둔다.
 */
export function refreshAssetUrl(key: string): Promise<boolean> {
  if (!key.startsWith("asset:")) return Promise.resolve(false);
  const running = refreshing.get(key);
  if (running) return running;
  if (refreshUsed.has(key)) return Promise.resolve(false);
  refreshUsed.add(key);
  const id = key.slice("asset:".length);
  const p = Promise.resolve()
    .then(() => refresher([id]))
    .then((r) => {
      const entry = r && r.asset_urls ? r.asset_urls[id] : null;
      if (!entry || !entry.url) { if (r) registerAssetUrls(r.asset_urls || {}, r.storage_origin); return false; }
      registerAssetUrls({ [id]: entry }, r && r.storage_origin);
      // 새 주소가 예전과 같아도(서명기가 같은 주소를 줌) 실패 상태면 한 번은 다시 받는다 — 1회 재시도 보장(refreshUsed 로 반복은 막힘)
      if (peekAsset(key).status === "error") invalidateCached(key);
      return true;
    })
    .catch((e: unknown) => { console.warn(`[twin-asset] ${key} 새 주소 받기 실패:`, e instanceof Error ? e.message : e); return false; })
    .then((ok) => {
      refreshing.delete(key);
      // 못 받았으면 — 만료 전에 미뤄 둔 열쇠는 지금 주소로라도 한 번 받게 다시 그린다(이미 error 면 그대로)
      if (!ok) { const w = waiting.get(key); if (w) { clearTimeout(w); waiting.delete(key); } nudgeAsset(key); }
      return ok;
    });
  refreshing.set(key, p);
  return p;
}

// 불러오기 결과를 본다 — ready 면 1회 기회 되살림 · 400/401/403 실패면(만료로 봄) 새 주소로 한 번 더
onAssetSettled((key, snap) => {
  if (!key.startsWith("asset:")) return;
  if (snap.status === "ready") { refreshUsed.delete(key); return; }
  if (snap.status === "error" && snap.httpStatus && [400, 401, 403].includes(snap.httpStatus)) void refreshAssetUrl(key);
});

/** asset_id → ResolvedAsset(주소가 없으면 null) — 만료가 가까우면 새 주소를 받는 동안 null(불러오는 중), 기회를 이미 썼으면 지금 주소로 */
export function resolveAsset(assetId: string): ResolvedAsset | null {
  const key = assetKeyForId(assetId);
  const k = known.get(key);
  if (!k || !k.url) return null;
  if (peekAsset(key).status === "idle" && expiringSoon(k) && (refreshing.has(key) || !refreshUsed.has(key))) { void refreshAssetUrl(key); return null; }
  return { key, url: k.url, assetId: assetId.toLowerCase(), ...(k.expiresAt ? { expiresAt: k.expiresAt } : {}) };
}

/** 정의의 자산 칸 → ResolvedAsset. url 은 그대로 · asset_id 는 resolveAsset. 아직 주소 없음 = null */
export function resolveAssetRef(a: Pick<AssetGeometry, "url" | "asset_id">): ResolvedAsset | null {
  if (typeof a.asset_id === "string" && a.asset_id) return resolveAsset(a.asset_id);
  if (typeof a.url === "string" && a.url) return { key: assetKeyForUrl(a.url), url: a.url };
  return null;
}

/** 서버가 준 검사 결과(보조 치수) — 없으면 null */
export function assetInspectionOf(key: string): AssetInspection | null {
  const k = known.get(key);
  return k ? k.inspection : null;
}

/**
 * 그 열쇠만 잊는다 — 다음 요청 때 다시 받는다(구독자가 다시 그림). 전역 비우기는 없다.
 *   STEP 2-C: 서명 URL 만료 · 자산 교체 → 서버에서 새 주소를 받아 registerAssetUrls 한 뒤 invalidateAsset(열쇠).
 */
export function invalidateAsset(key: string) { invalidateCached(key); }

/** 지금 열쇠의 캐시 상태(편의) */
export const assetStateOf = (key: string) => peekAsset(key);

/** 테스트 전용 */
export function resetAssetResolverForTest(unresolvedMs?: number) { known.clear(); waiting.forEach((t) => clearTimeout(t)); waiting.clear(); refreshUsed.clear(); refreshing.clear(); if (unresolvedMs !== undefined) UNRESOLVED_MS = unresolvedMs; }
/** 테스트 전용 — 새 주소 받기(null = 기본 GET asset-urls) */
export function setAssetUrlRefresherForTest(fn: AssetUrlRefresher | null) { refresher = fn || defaultRefresher; }
